'use client'

import { useEffect, useState } from 'react'

/**
 * Abonnements aux chaînes (youtubeurs) — natif BLC.
 *
 * Modèle : un TOKEN d'appareil (localStorage 'blc.recoToken') identifie « mes
 * abonnements », indépendamment du login. Les abonnements sont stockés :
 *   - en localStorage (affichage instantané, marche hors-ligne),
 *   - ET côté serveur (POST/DELETE /api/subscriptions avec le token) → lisibles
 *     par l'API de recommandations, y compris par Jarvis Ultimate V2.
 *
 * Le token est aussi le « code de connexion Jarvis » : Jarvis appelle
 * /api/reco/video?token=<token> pour récupérer les vidéos des abonnements.
 */
export type SubType = 'artist' | 'channel'
export type Subscription = { channelId: string; title: string; thumbnail?: string; type: SubType; addedAt: number }

const KEY = 'blc.subscriptions.v1'
const TOKEN_KEY = 'blc.recoToken'

/** Token d'appareil stable (créé au 1er besoin). Sert de handle pour Jarvis. */
export function getRecoToken(): string {
  if (typeof localStorage === 'undefined') return ''
  try {
    let t = localStorage.getItem(TOKEN_KEY)
    if (!t) {
      t = (crypto?.randomUUID?.() || 'tok_' + Math.random().toString(36).slice(2) + Date.now().toString(36)).replace(/-/g, '')
      localStorage.setItem(TOKEN_KEY, t)
    }
    return t
  } catch { return '' }
}

function read(): Subscription[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const list = JSON.parse(localStorage.getItem(KEY) || '[]') as Subscription[]
    // rétro-compat : les anciens abonnements sans `type` deviennent 'channel'.
    return list.map(s => ({ ...s, type: (s.type === 'artist' ? 'artist' : 'channel') as SubType }))
  } catch { return [] }
}
function write(list: Subscription[]) {
  try { localStorage.setItem(KEY, JSON.stringify(list)); window.dispatchEvent(new Event('blc-subscriptions')) } catch {}
}

export function getSubscriptions(): Subscription[] { return read() }
export function isSubscribed(channelId: string): boolean {
  return read().some(s => s.channelId === channelId)
}

/** Devine si une chaîne est un ARTISTE musical d'après son nom (Topic/VEVO). */
export function guessType(title: string): SubType {
  return /\s-\s*topic$/i.test(title || '') || /vevo$/i.test(title || '') ? 'artist' : 'channel'
}

/** Envoie l'abonnement au serveur (upsert avec le type). */
function pushServer(ch: { channelId: string; title: string; thumbnail?: string; type: SubType }) {
  const token = getRecoToken()
  fetch('/api/subscriptions', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, channelId: ch.channelId, title: ch.title || '', thumbnail: ch.thumbnail || '', type: ch.type }),
  }).catch(() => {})
}

/** S'abonner (idempotent). Écrit local + serveur. type auto-deviné si omis. */
export function subscribe(ch: { channelId: string; title: string; thumbnail?: string; type?: SubType }) {
  if (!ch?.channelId) return
  const list = read()
  if (list.some(s => s.channelId === ch.channelId)) return
  const type: SubType = ch.type || guessType(ch.title || '')
  write([{ channelId: ch.channelId, title: ch.title || '', thumbnail: ch.thumbnail, type, addedAt: Date.now() }, ...list])
  pushServer({ channelId: ch.channelId, title: ch.title || '', thumbnail: ch.thumbnail, type })
}

/** Ajoute en une fois une liste d'abonnements (import extension / YouTube).
 *  Ne touche pas aux abonnements existants. Renvoie le nombre ajouté. */
export function mergeSubscriptions(chs: { channelId: string; title: string; thumbnail?: string }[]): number {
  const list = read()
  const have = new Set(list.map(s => s.channelId))
  const fresh = chs.filter(c => c?.channelId && !have.has(c.channelId) && have.add(c.channelId))
    .map(c => ({ channelId: c.channelId, title: c.title || '', thumbnail: c.thumbnail, type: guessType(c.title || ''), addedAt: Date.now() }))
  if (!fresh.length) return 0
  write([...fresh, ...list])
  // Envoi serveur étalé (l'API prend un abonnement à la fois).
  fresh.forEach((c, i) => setTimeout(() => pushServer(c), i * 120))
  return fresh.length
}

/** (Re)catégorise un abonnement : artiste ↔ chaîne. */
export function setSubscriptionType(channelId: string, type: SubType) {
  const list = read()
  const s = list.find(x => x.channelId === channelId)
  if (!s) return
  write(list.map(x => x.channelId === channelId ? { ...x, type } : x))
  pushServer({ channelId, title: s.title, thumbnail: s.thumbnail, type })
}

/** Se désabonner. Écrit local + serveur. */
export function unsubscribe(channelId: string) {
  write(read().filter(s => s.channelId !== channelId))
  const token = getRecoToken()
  fetch(`/api/subscriptions?token=${encodeURIComponent(token)}&channelId=${encodeURIComponent(channelId)}`, { method: 'DELETE' }).catch(() => {})
}

export function toggleSubscribe(ch: { channelId: string; title: string; thumbnail?: string; type?: SubType }) {
  if (isSubscribed(ch.channelId)) unsubscribe(ch.channelId)
  else subscribe(ch)
}

/** Type d'un abonnement (ou null si pas abonné). */
export function subscriptionType(channelId: string): SubType | null {
  return read().find(s => s.channelId === channelId)?.type ?? null
}

/** Hook réactif : liste des abonnements. */
export function useSubscriptions() {
  const [subs, setSubs] = useState<Subscription[]>([])
  useEffect(() => {
    const load = () => setSubs(read())
    load()
    window.addEventListener('blc-subscriptions', load)
    window.addEventListener('storage', load)
    return () => { window.removeEventListener('blc-subscriptions', load); window.removeEventListener('storage', load) }
  }, [])
  return subs
}

/** Hook : est-on abonné à une chaîne (réactif) ? */
export function useIsSubscribed(channelId: string | undefined | null) {
  const subs = useSubscriptions()
  return !!channelId && subs.some(s => s.channelId === channelId)
}
